import React from 'react';
import { useLayout } from '../contexts/LayoutContext';

/**
 * Save/restore helpers for the right pane width around temporary panes.
 *
 * Call saveWidth() before mounting a temporary pane, then restoreWidth() when
 * closing it. This prevents the temporary pane's resized width from being
 * persisted as the user's preferred width for normal panes.
 *
 * Both returned functions are stable references (useCallback with stable deps).
 */
export function useTemporaryRightPaneWidth(): {
  saveWidth: () => void;
  restoreWidth: () => void;
} {
  const { rightPaneWidth, setRightPaneWidth } = useLayout();

  // Always-current ref so saveWidth() can capture the live value without
  // needing to be re-created on every width change.
  const widthRef = React.useRef(rightPaneWidth);
  widthRef.current = rightPaneWidth;

  const savedRef = React.useRef<number | null>(null);

  const saveWidth = React.useCallback(() => {
    savedRef.current = widthRef.current;
  }, []);

  const restoreWidth = React.useCallback(() => {
    if (savedRef.current !== null) {
      setRightPaneWidth(savedRef.current);
      savedRef.current = null;
    }
  }, [setRightPaneWidth]);

  return { saveWidth, restoreWidth };
}
